import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { MODULE_CATALOGUE, type LicenceDeliveryState, type LifecycleDto } from '@his/shared';
import { downloadFile, get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateOnly, downloadCsv, humanize, todayIso } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { ActionMenu, ActionMenuItem, ActionMenuSeparator, Badge, Button, Field, Input, Select } from '@/components/ui/primitives';
import { CopyMastersDialog } from './CopyMastersDialog';
import { FacilityGroupDialog } from './FacilityGroupDialog';
import { useToast } from '@/components/ui/toast';
import { FacilityRowActions } from './FacilityRowActions';
import { LOCK_STATE_BADGE } from './TenantDeploymentPanel';
import { EmailQuotaDialog, OWNER_EMAIL_PAUSED_KEY } from './EmailQuotaDialog';
import type { OwnerPausedTenantDto } from '@his/shared';
import { useOwnerCapability } from './owner-nav';

const FACILITY_TYPES = [
  { value: 'hospital', label: 'Hospital' },
  { value: 'lab', label: 'Laboratory' },
  { value: 'pharmacy', label: 'Medical store' },
] as const;

const PAGE_SIZE = 50;
const DAY_MS = 24 * 60 * 60 * 1000;

function facilityLabel(type: string): string {
  return FACILITY_TYPES.find((t) => t.value === type)?.label ?? type;
}

function moduleName(code: string): string {
  return MODULE_CATALOGUE.find((entry) => entry.code === code)?.name ?? code;
}

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  facilityType: 'hospital' | 'lab' | 'pharmacy';
  parentTenantId: string | null;
  parentName: string | null;
  childCount: number;
  planName: string | null;
  currency: string;
  readOnlyGrace: boolean;
  trialEndsAt: string | null;
  createdAt: string;
  /** Offline distribution (docs/SYNC.md): where the hospital writes and whether its install is read-only. */
  deploymentMode: 'CLOUD' | 'LOCAL';
  lockState: 'NONE' | 'STALE' | 'MISMATCH' | 'VERSION';
  runningVersion: string | null;
  lastSeenAt: string | null;
  daysUntilLock: number | null;
  /** Owner toggle "Allow offline mode" (default off). */
  offlineEnabled: boolean;
  environment?: 'live' | 'demo';
  packages?: string[];
  addOns?: string[];
  lifecycle?: LifecycleDto | null;
  closureEndsAt?: string | null;
  licenceDelivery?: LicenceDeliveryState | null;
}

interface TenantPage {
  data: TenantRow[];
  nextCursor: string | null;
}

function daysUntil(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  return Math.max(0, Math.ceil((Date.parse(iso) - now) / DAY_MS));
}

export function lifecycleChip(row: Pick<TenantRow, 'status' | 'lifecycle' | 'closureEndsAt' | 'trialEndsAt'>, now: number = Date.now()): {
  label: string;
  variant: 'success' | 'warning' | 'destructive' | 'muted' | 'secondary';
} {
  const lifecycle = row.lifecycle;
  const state = lifecycle?.state ?? row.status;
  switch (state) {
    case 'closing': {
      const days = daysUntil(lifecycle?.until ?? row.closureEndsAt, now);
      return { label: days === null ? 'Closing' : `Closes in ${days} d`, variant: 'warning' };
    }
    case 'deletion_grace': {
      const days = daysUntil(lifecycle?.deletionDueAt, now);
      return { label: days === null ? 'Deletion pending' : `Deleted in ${days} d`, variant: 'destructive' };
    }
    case 'trial': {
      const days = daysUntil(lifecycle?.trialEndsAt ?? row.trialEndsAt, now);
      return { label: days === null ? 'Trial' : `Trial, ${days} d left`, variant: 'secondary' };
    }
    case 'suspended':
      return { label: lifecycle?.frozen ? 'Frozen' : 'Suspended', variant: 'destructive' };
    case 'past_due':
      return { label: 'Past due', variant: 'warning' };
    case 'archived':
      return { label: 'Archived', variant: 'muted' };
    default:
      return { label: 'Active', variant: 'success' };
  }
}

const DELIVERY_BADGE: Record<LicenceDeliveryState, { label: string; variant: 'success' | 'warning' | 'destructive' | 'muted' }> = {
  applied: { label: 'Licence applied', variant: 'success' },
  waiting: { label: 'Licence waiting', variant: 'warning' },
  rejected: { label: 'Licence rejected', variant: 'destructive' },
  legacy: { label: 'Old server', variant: 'muted' },
};

/** Tenant registry and provisioning (SRS 5.1, 13.5). */
export function TenantsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [facilityType, setFacilityType] = useState('');
  const [copyInto, setCopyInto] = useState<TenantRow | null>(null);
  const [grouping, setGrouping] = useState<TenantRow | null>(null);
  const [emailTenant, setEmailTenant] = useState<TenantRow | null>(null);
  const canEmail = useOwnerCapability('email.read');
  const emailPaused = useQuery({
    queryKey: OWNER_EMAIL_PAUSED_KEY,
    queryFn: () => get<OwnerPausedTenantDto[]>('/owner/email/paused'),
    enabled: canEmail,
    staleTime: 60_000,
  });
  const pausedEmail = new Set((emailPaused.data ?? []).map((row) => row.tenantId));
  const canProvision = useOwnerCapability('tenant.provision');

  const tenants = useInfiniteQuery({
    queryKey: ['tenants', search, status, facilityType],
    queryFn: ({ pageParam }) =>
      get<TenantPage>(
        `/platform/tenants${qs({
          q: search || undefined,
          status: status || undefined,
          facilityType: facilityType || undefined,
          limit: PAGE_SIZE,
          cursor: pageParam ?? undefined,
        })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const rows = tenants.data?.pages.flatMap((page) => page.data) ?? [];

  // The file is built from the rows already on screen, so it can never show
  // something other than what was being looked at.
  const exportGrid = (format: 'xlsx' | 'pdf') =>
    downloadFile(
      '/platform/exports/grid',
      {
        format,
        title: 'Facilities',
        subtitle: 'Every hospital, lab and medical store on the platform',
        meta: [
          ...(search ? [{ label: 'Search', value: search }] : []),
          ...(facilityType ? [{ label: 'Type', value: facilityLabel(facilityType) }] : []),
          ...(status ? [{ label: 'Status', value: humanize(status, 'tenant_status') }] : []),
        ],
        columns: [
          { key: 'name', label: 'Facility', kind: 'text' },
          { key: 'slug', label: 'Subdomain', kind: 'mono' },
          { key: 'facilityTypeLabel', label: 'Type', kind: 'status' },
          { key: 'group', label: 'Group', kind: 'text' },
          { key: 'packageNames', label: 'Packages', kind: 'text' },
          { key: 'lifecycleLabel', label: 'Status', kind: 'status' },
          { key: 'deployment', label: 'Deployment', kind: 'status' },
          { key: 'runningVersion', label: 'Version', kind: 'mono' },
          { key: 'lastSeenAt', label: 'Last seen', kind: 'datetime' },
          { key: 'createdAt', label: 'Created', kind: 'date' },
        ],
        rows: rows.map((row) => ({
          ...row,
          facilityTypeLabel: facilityLabel(row.facilityType),
          group: row.parentName ?? (row.childCount > 0 ? `Heads ${row.childCount}` : ''),
          packageNames: (row.packages ?? []).join(', '),
          lifecycleLabel: lifecycleChip(row).label,
          deployment: row.deploymentMode === 'LOCAL' ? 'Offline' : 'Online',
        })),
      },
      `facilities-${todayIso()}.${format}`,
    ).catch((caught) => toast.error('Could not build the file', describeError(caught)));

  return (
    <>
      <PageHeader
        title="Facilities"
        description="Every hospital, lab and medical store on the platform"
        actions={canProvision && <Button onClick={() => navigate('/owner/tenants/new')}>Provision facility</Button>}
      />

      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search name or subdomain' }}
        filters={
          <>
            <FilterField label="Type">
              <Select className="w-44" value={facilityType} onChange={(e) => setFacilityType(e.target.value)}>
                <option value="">All types</option>
                {FACILITY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label="Status">
              <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {['trial', 'active', 'past_due', 'suspended', 'archived'].map((s) => (
                  <option key={s} value={s}>
                    {humanize(s, 'tenant_status')}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        trailing={
          <ActionMenu label="Export this list" align="end">
            <ActionMenuItem icon={<FileSpreadsheet className="h-4 w-4" />} onSelect={() => void exportGrid('xlsx')}>
              Excel workbook
            </ActionMenuItem>
            <ActionMenuItem icon={<FileText className="h-4 w-4" />} onSelect={() => void exportGrid('pdf')}>
              PDF
            </ActionMenuItem>
            <ActionMenuSeparator />
            <ActionMenuItem
              icon={<Download className="h-4 w-4" />}
              onSelect={() =>
                downloadCsv(`facilities-${todayIso()}`, rows, [
                  { header: 'Facility', value: (row) => row.name },
                  { header: 'Subdomain', value: (row) => row.slug },
                  { header: 'Type', value: (row) => facilityLabel(row.facilityType) },
                  { header: 'Environment', value: (row) => humanize(row.environment ?? 'live', 'tenant_environment') },
                  { header: 'Group', value: (row) => row.parentName ?? (row.childCount > 0 ? 'Flagship' : '') },
                  { header: 'Packages', value: (row) => (row.packages ?? []).join('; ') },
                  { header: 'Add-ons', value: (row) => (row.addOns ?? []).map(moduleName).join('; ') },
                  { header: 'Status', value: (row) => lifecycleChip(row).label },
                  { header: 'Deployment', value: (row) => (row.deploymentMode === 'LOCAL' ? 'Offline' : 'Online') },
                  { header: 'Offline allowed', value: (row) => (row.offlineEnabled ? 'Yes' : 'No') },
                  { header: 'Running version', value: (row) => row.runningVersion ?? '' },
                  { header: 'Last seen', value: (row) => (row.lastSeenAt ? dateOnly(row.lastSeenAt) : '') },
                  { header: 'Created', value: (row) => dateOnly(row.createdAt) },
                ])
              }
            >
              CSV
            </ActionMenuItem>
          </ActionMenu>
        }
        onReset={
          search || status || facilityType
            ? () => {
                setSearch('');
                setStatus('');
                setFacilityType('');
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={tenants.isLoading} refreshing={tenants.isFetching}
        error={tenants.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/owner/tenants/${row.id}`)}
        empty="Nothing here yet. Provision the first facility to get started."
        columns={[
          {
            header: 'Facility',
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <p className="font-medium">
                  {row.name} {row.environment === 'demo' && <Badge variant="secondary">Demo</Badge>}
                </p>
                <p className="font-mono text-xs text-muted-foreground">{row.slug}</p>
              </div>
            ),
          },
          {
            header: 'Type',
            sort: (row) => row.facilityType,
            cell: (row) => (
              <div className="space-y-0.5">
                <Badge variant="secondary">{facilityLabel(row.facilityType)}</Badge>
                {row.parentName && <p className="text-xs text-muted-foreground">Part of {row.parentName}</p>}
                {row.childCount > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Heads {row.childCount} site{row.childCount === 1 ? '' : 's'}
                  </p>
                )}
              </div>
            ),
          },
          {
            header: 'Packages',
            cell: (row) => {
              const packages = row.packages ?? (row.planName ? [row.planName] : []);
              return (
                <div className="flex max-w-[260px] flex-wrap gap-1">
                  {packages.length === 0 && <span className="text-muted-foreground">None</span>}
                  {packages.map((name) => (
                    <Badge key={name} variant="muted">
                      {name}
                    </Badge>
                  ))}
                  {(row.addOns ?? []).map((code) => (
                    <Badge key={code} variant="default">
                      {moduleName(code)}
                    </Badge>
                  ))}
                </div>
              );
            },
          },
          {
            header: 'Status',
            sort: (row) => lifecycleChip(row).label,
            cell: (row) => {
              const chip = lifecycleChip(row);
              return (
                <div className="flex flex-wrap gap-1">
                  <Badge variant={chip.variant}>{chip.label}</Badge>
                  {row.readOnlyGrace && <Badge variant="warning">Read-only</Badge>}
                </div>
              );
            },
          },
          {
            header: 'Deployment',
            sort: (row) => row.deploymentMode,
            cell: (row) => (
              <div className="space-y-0.5">
                <div className="flex flex-wrap gap-1">
                  <Badge variant={row.deploymentMode === 'LOCAL' ? 'default' : 'muted'}>
                    {row.deploymentMode === 'LOCAL' ? 'Offline' : 'Online'}
                  </Badge>
                  {row.lockState !== 'NONE' && <Badge variant="destructive">{LOCK_STATE_BADGE[row.lockState].label}</Badge>}
                  {row.licenceDelivery && (
                    <Badge variant={DELIVERY_BADGE[row.licenceDelivery].variant}>{DELIVERY_BADGE[row.licenceDelivery].label}</Badge>
                  )}
                </div>
                {row.deploymentMode === 'LOCAL' && (
                  <p className="text-xs text-muted-foreground">
                    {row.runningVersion ? `v${row.runningVersion} · ` : ''}
                    {row.lastSeenAt ? `Seen ${dateOnly(row.lastSeenAt)}` : 'Never checked in'}
                    {row.daysUntilLock !== null ? ` · ${row.daysUntilLock}d left` : ''}
                  </p>
                )}
                {row.deploymentMode !== 'LOCAL' && row.offlineEnabled && <p className="text-xs text-muted-foreground">Offline mode allowed</p>}
              </div>
            ),
          },
          ...(canEmail
            ? [
                {
                  header: 'Email',
                  cell: (row: TenantRow) => (
                    <button
                      type="button"
                      className="focus-ring rounded"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEmailTenant(row);
                      }}
                    >
                      <Badge variant={pausedEmail.has(row.id) ? 'warning' : 'muted'}>{pausedEmail.has(row.id) ? 'Email paused' : 'Email'}</Badge>
                    </button>
                  ),
                },
              ]
            : []),
          { header: 'Created', sort: (row) => row.createdAt, cell: (row) => dateOnly(row.createdAt) },
          {
            header: '',
            align: 'right',
            width: '60px',
            cell: (row) => <FacilityRowActions facility={row} onGroup={() => setGrouping(row)} onCopyInto={() => setCopyInto(row)} />,
          },
        ]}
        footer={
          tenants.hasNextPage && (
            <Button variant="outline" size="sm" onClick={() => void tenants.fetchNextPage()} loading={tenants.isFetchingNextPage}>
              Show more
            </Button>
          )
        }
      />

      {emailTenant && <EmailQuotaDialog tenant={emailTenant} onClose={() => setEmailTenant(null)} />}

      {grouping && (
        <FacilityGroupDialog
          tenant={grouping}
          open={Boolean(grouping)}
          onClose={() => setGrouping(null)}
        />
      )}
      {copyInto && (
        <CopyMastersDialog
          tenant={copyInto}
          open={Boolean(copyInto)}
          onClose={() => setCopyInto(null)}
        />
      )}
    </>
  );
}

const LIMIT_LABELS: Record<string, string> = {
  users: 'Users',
  beds: 'Beds',
  branches: 'Branches',
  storageMb: 'Storage (MB)',
  smsCredits: 'SMS credits',
};

export type LimitFieldValues = Record<keyof typeof LIMIT_LABELS, string>;

export function LimitFields({
  form,
  onChange,
}: {
  form: Record<string, unknown>;
  onChange: (patch: Record<string, string>) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-5">
      {Object.entries(LIMIT_LABELS).map(([key, label]) => (
        <Field key={key} label={label}>
          <Input
            type="number"
            min={0}
            placeholder="∞"
            value={String(form[key] ?? '')}
            onChange={(e) => onChange({ [key]: e.target.value })}
          />
        </Field>
      ))}
    </div>
  );
}

export function readLimits(form: Record<string, unknown>) {
  const out: Record<string, number | null> = {};
  for (const key of Object.keys(LIMIT_LABELS)) {
    const raw = form[key];
    out[key] = raw ? Number(raw) : null;
  }
  return out;
}
